import { Wind, CloudRain, Droplets, Thermometer, Eye, Gauge } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

interface EnvTrendsPanelProps {
  aqiForecast: any;
  hourly: { dt: number; pop: number; rain?: { "1h"?: number }; snow?: { "1h"?: number }; temp?: number; humidity?: number; wind_speed?: number; visibility?: number; pressure?: number }[];
}

function getAqiInfo(aqi: number) {
  if (aqi <= 1) return { label: "Good", color: "text-green-400", bg: "bg-green-500/10", bar: "rgb(74 222 128)" };
  if (aqi <= 2) return { label: "Fair", color: "text-yellow-400", bg: "bg-yellow-500/10", bar: "rgb(234 179 8)" };
  if (aqi <= 3) return { label: "Moderate", color: "text-orange-400", bg: "bg-orange-500/10", bar: "rgb(249 115 22)" };
  if (aqi <= 4) return { label: "Poor", color: "text-red-400", bg: "bg-red-500/10", bar: "rgb(239 68 68)" };
  return { label: "Hazardous", color: "text-purple-400", bg: "bg-purple-500/10", bar: "rgb(168 85 247)" };
}

function formatTime(ts: number) {
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function EnvTrendsPanel({ aqiForecast, hourly }: EnvTrendsPanelProps) {
  const hasAqi = aqiForecast?.list && aqiForecast.list.length > 0;
  const next12 = hourly.slice(0, 12);

  const precipData = next12.map((h) => ({
    time: new Date(h.dt * 1000).toLocaleTimeString([], { hour: "numeric" }).replace(" ", ""),
    pop: Math.round(h.pop * 100),
    rain: h.rain?.["1h"] || 0,
    snow: h.snow?.["1h"] || 0,
  }));

  const totalRain = precipData.reduce((sum, d) => sum + d.rain, 0);
  const peakPop = Math.max(...precipData.map((d) => d.pop));
  const wettest = precipData.reduce((max, d) => (d.pop > max.pop ? d : max), precipData[0]);

  const aqiHourly = hasAqi ? aqiForecast.list.slice(0, 24) : [];
  const currentAqi = hasAqi ? (aqiForecast.list[0]?.main?.aqi ?? 1) : 1;
  const aqiInfo = getAqiInfo(currentAqi);
  const aqiImproving = aqiHourly.length > 12
    ? (aqiHourly[aqiHourly.length - 1]?.main?.aqi ?? 1) < currentAqi
    : false;

  const avgHumidity = next12.length > 0
    ? Math.round(next12.reduce((s, h) => s + (h.humidity ?? 0), 0) / next12.filter(h => h.humidity !== undefined).length)
    : null;
  const avgWind = next12.length > 0
    ? (next12.reduce((s, h) => s + (h.wind_speed ?? 0), 0) / next12.filter(h => h.wind_speed !== undefined).length).toFixed(1)
    : null;
  const avgTemp = next12.length > 0 && next12[0].temp !== undefined
    ? Math.round(next12.reduce((s, h) => s + (h.temp ?? 0), 0) / next12.filter(h => h.temp !== undefined).length)
    : null;

  return (
    <div className="rounded-2xl border border-border bg-card">
      <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Environmental Trends</h3>
        <div className="flex items-center gap-3">
          {hasAqi && (
            <div className="flex items-center gap-1.5">
              <div className={`h-2 w-2 rounded-full ${aqiInfo.bg.replace("/10", "")}`} style={{ backgroundColor: aqiInfo.bar }} />
              <span className={`text-[10px] font-semibold ${aqiInfo.color}`}>AQI {currentAqi}</span>
            </div>
          )}
          {peakPop > 0 && (
            <div className="flex items-center gap-1.5">
              <Droplets className="h-3 w-3 text-blue-400" />
              <span className="text-[10px] font-semibold text-blue-400">{peakPop}% peak</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 space-y-5">
        {hasAqi && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${aqiInfo.bg}`}>
                  <Wind className={`h-4 w-4 ${aqiInfo.color}`} />
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Air Quality Index</h4>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="text-xl font-bold">{currentAqi}</span>
                    <span className={`text-xs font-semibold ${aqiInfo.color}`}>{aqiInfo.label}</span>
                    {aqiImproving && (
                      <span className="text-[10px] text-emerald-400 ml-1">↓ Improving</span>
                    )}
                  </div>
                </div>
              </div>
              {aqiHourly.length > 0 && (
                <div className="text-right text-[10px] text-muted-foreground">
                  <p>Next 24h forecast</p>
                  <p className="mt-0.5">
                    {aqiHourly.filter((h: any) => (h.main?.aqi ?? 1) <= 2).length} of 24h good/fair
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-end gap-px h-12">
              {aqiHourly.map((h: any, i: number) => {
                const aqi = h.main?.aqi ?? 1;
                const barHeight = Math.max(6, (aqi / 5) * 100);
                return (
                  <div
                    key={i}
                    className="flex-1 min-w-[3px] rounded-t-sm transition-colors"
                    style={{
                      height: `${barHeight}%`,
                      backgroundColor:
                        aqi <= 1 ? "rgb(74 222 128)" :
                        aqi <= 2 ? "rgb(234 179 8)" :
                        aqi <= 3 ? "rgb(249 115 22)" :
                        aqi <= 4 ? "rgb(239 68 68)" :
                        "rgb(168 85 247)",
                    }}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
              <span>Now</span>
              <span>+12h</span>
              <span>+24h</span>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10">
                <CloudRain className="h-4 w-4 text-blue-400" />
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Precipitation · Next 12h</h4>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-xl font-bold text-blue-400">{peakPop}%</span>
                  <span className="text-xs text-muted-foreground">peak chance</span>
                  {totalRain > 0 && (
                    <span className="text-xs text-muted-foreground">· {totalRain.toFixed(1)} mm total</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="h-28">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={precipData} margin={{ top: 5, right: 5, bottom: 0, left: -25 }}>
                <defs>
                  <linearGradient id="popGradEnv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(0.65 0.2 240)" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="oklch(0.65 0.2 240)" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} tickLine={false} axisLine={false} unit="%" domain={[0, 100]} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8, fontSize: 12,
                    border: "1px solid var(--border)",
                    background: "var(--popover)",
                    color: "var(--popover-foreground)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                  }}
                  labelStyle={{ color: "var(--foreground)", fontWeight: 600 }}
                  cursor={{ stroke: "var(--border)", strokeWidth: 1 }}
                />
                <ReferenceLine y={50} stroke="oklch(1 0 0 / 0.1)" strokeDasharray="2 2" />
                <Area
                  type="monotone"
                  dataKey="pop"
                  stroke="oklch(0.7 0.2 240)"
                  strokeWidth={2}
                  fill="url(#popGradEnv)"
                  name="Chance"
                  unit="%"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {peakPop > 30 && wettest && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-blue-500/5 border border-blue-500/20 px-3 py-2">
              <Droplets className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />
              <p className="text-[11px] text-muted-foreground">
                Wettest hour around <span className="font-semibold text-foreground">{wettest.time}</span> ({wettest.pop}% chance)
              </p>
            </div>
          )}
        </div>

        {(avgTemp !== null || avgHumidity !== null || avgWind !== null) && (
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border/40">
            {avgTemp !== null && (
              <div className="flex items-center gap-2">
                <Thermometer className="h-3.5 w-3.5 text-primary/60" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg Temp</p>
                  <p className="text-sm font-bold">{avgTemp}°</p>
                </div>
              </div>
            )}
            {avgHumidity !== null && (
              <div className="flex items-center gap-2">
                <Droplets className="h-3.5 w-3.5 text-primary/60" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg Humidity</p>
                  <p className="text-sm font-bold">{avgHumidity}%</p>
                </div>
              </div>
            )}
            {avgWind !== null && (
              <div className="flex items-center gap-2">
                <Wind className="h-3.5 w-3.5 text-primary/60" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg Wind</p>
                  <p className="text-sm font-bold">{avgWind} m/s</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}